/* eslint-disable no-await-in-loop, no-restricted-syntax, no-continue */
/**
 * Replay engine — picks up `ready_to_sync` sessions and walks them
 * through the step sequence in design §3.8 to land them on the
 * server.
 *
 * Lint exemptions at the top of this file:
 *   - no-await-in-loop / no-restricted-syntax (no for-of) / no-continue:
 *     this engine MUST process steps sequentially. Each HTTP call
 *     produces an id the next step needs, idempotency keys make
 *     retries safe, and the bookmark advances per step. Concurrent
 *     replay would race the bookmark write. The default eslint rules
 *     here optimise for parallelisable list operations; that's the
 *     wrong shape for a sequential replay engine.
 *
 * Properties (the data-safety contract):
 *   - **Idempotent.** Each step either uses an idempotency_key (steps
 *     3, 4) or is naturally idempotent (PATCHes are last-write-wins,
 *     register-bulk replaces all defects). A retried step never
 *     creates duplicates.
 *   - **Bookmark-based resume.** The session's `replay_bookmark` is
 *     written to IndexedDB BEFORE each step is attempted. An
 *     interrupted replay (network drop, tab close) picks up at the
 *     bookmarked step on next connectivity.
 *   - **Sequential.** One session at a time, oldest first. No
 *     concurrent replays — both to keep the picture deterministic
 *     for review and to avoid hammering a recovering connection.
 *   - **Error-class aware.** Network failures pause and retry on the
 *     next connectivity event. 4xx mark the session `failed`
 *     (config drift, missing pallet, server validation). 5xx retry
 *     with exponential backoff up to 3 attempts before pausing.
 *
 * Step 7 (photo upload) is reserved but not implemented yet — Phase 5
 * does not capture photos to the offline session, so there are no
 * blobs to ship at replay time. Adding photo capture + upload is
 * tracked as a follow-up and will plug in here.
 */

import { stageAllocationRequests } from "../actions/Layers/stageAllocation";
import apiClient from "../constants/apiClient";
import { IS_DEMO, ROOT_URL } from "../constants/urls";
import {
    deleteSession,
    getActiveSessionId,
    getSessionsByStatus,
    setActiveSessionId,
    updateReplayBookmark,
} from "./sessionManager";
import type {
    OfflineCheckSession,
    OfflineFruit,
    ReplayIdMap,
} from "./types";

const API_BASE = `${ROOT_URL}api`;

/**
 * Step boundary names — used by the IsOfflineBar progress display
 * (Phase 6.3). The total step count is `STEPS.length`.
 *
 * `replay_bookmark` is an index into this array and is persisted with
 * the session, so new steps go at the end: inserting one shifts the
 * meaning of every bookmark after it for sessions already queued.
 *
 * Exception: `apply_business_rules` was inserted before `complete_check`,
 * because `/complete` can send the rule-derived status/category to an
 * external service, so the rules must run first (as CheckSummary does
 * online). A session queued at the old grouping index (10) now resumes
 * at `complete_check`: it re-runs `/complete` once (idempotent, but it
 * re-sends the external status) and then groups.
 */
export const STEPS = [
    "verify_config",
    "resolve_barcode",
    "fetch_layer",
    "create_check",
    "create_fruits",
    "patch_fruit_fields",
    "register_defects",
    "upload_photos",
    "patch_check_meta",
    "apply_business_rules",
    "complete_check",
    "group_matching_siblings",
    "allocate_to_stage",
] as const;
export type StepName = (typeof STEPS)[number];
export const STEP_COUNT = STEPS.length;

/**
 * UI progress hooks. Phase 6.3 wires these to a React state holder
 * that drives the IsOfflineBar's "Syncing check... (step X/Y)"
 * messaging. Module-level so the engine can fire updates without
 * passing a callback down through every step.
 */
export interface ReplayProgress {
    sessionId: string;
    step: number;
    label: StepName;
    /** Total queued sessions visible to this batch. */
    queueLength: number;
    /** Index within this batch (0-based). */
    queueIndex: number;
}
export interface ReplayFailure {
    sessionId: string;
    reason: string;
}
/**
 * Fired once per session that completes all steps and is deleted
 * from IndexedDB. `cachedLayerId` is the cached_layer.id captured at
 * scan time — the same id present in the route param when the
 * controller is on the layer detail page (`/layer/:layer_id`). The
 * layer detail page subscribes to this to refresh itself when the
 * synced check belongs to the layer it is currently showing, so the
 * controller sees the just-synced check in the layer's history
 * without having to navigate away and back.
 */
export interface ReplaySuccess {
    sessionId: string;
    cachedLayerId: string;
}

type ProgressListener = (p: ReplayProgress | null) => void;
type FailureListener = (f: ReplayFailure | null) => void;
type SuccessListener = (s: ReplaySuccess) => void;

const progressListeners: Set<ProgressListener> = new Set();
const failureListeners: Set<FailureListener> = new Set();
const successListeners: Set<SuccessListener> = new Set();

export function onReplayProgress(cb: ProgressListener): () => void {
    progressListeners.add(cb);
    return () => progressListeners.delete(cb);
}
export function onReplayFailure(cb: FailureListener): () => void {
    failureListeners.add(cb);
    return () => failureListeners.delete(cb);
}
export function onReplaySuccess(cb: SuccessListener): () => void {
    successListeners.add(cb);
    return () => successListeners.delete(cb);
}
function emitProgress(p: ReplayProgress | null): void {
    progressListeners.forEach((cb) => {
        try {
            cb(p);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error("[replay] progress listener threw", err);
        }
    });
}
function emitFailure(f: ReplayFailure | null): void {
    failureListeners.forEach((cb) => {
        try {
            cb(f);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error("[replay] failure listener threw", err);
        }
    });
}
function emitSuccess(s: ReplaySuccess): void {
    successListeners.forEach((cb) => {
        try {
            cb(s);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error("[replay] success listener threw", err);
        }
    });
}

let processing = false;

/**
 * Drain the ready_to_sync queue. Idempotent and concurrency-safe: a
 * second invocation while a replay is in flight is a no-op (returns
 * immediately).
 *
 * Called by:
 *   - The connectivity monitor's offline→online transition (Phase 6.2).
 *   - App startup if there are pending sessions and we're online
 *     (covers the close-tab-while-offline edge case where no
 *     transition event will ever fire).
 */
export async function processQueue(): Promise<void> {
    if (processing) return;
    processing = true;
    try {
        const queue = (await getSessionsByStatus("ready_to_sync")).sort(
            (a, b) => a.created_at - b.created_at,
        );
        for (let i = 0; i < queue.length; i += 1) {
            const session = queue[i];
            await replayOneSession(session, i, queue.length);
        }
    } finally {
        processing = false;
        emitProgress(null);
    }
}

async function replayOneSession(
    initialSession: OfflineCheckSession,
    queueIndex: number,
    queueLength: number,
): Promise<void> {
    let session = await transitionToSyncing(initialSession);

    while (session.replay_bookmark < STEP_COUNT) {
        const step = session.replay_bookmark;
        emitProgress({
            sessionId: session.id,
            step,
            label: STEPS[step],
            queueIndex,
            queueLength,
        });

        const outcome = await runStep(session, step);
        if (outcome.kind === "ok") {
            session = await advanceBookmark(session, step + 1, outcome.idMap);
        } else if (outcome.kind === "fail") {
            await markFailed(session, outcome.reason);
            emitFailure({ sessionId: session.id, reason: outcome.reason });
            return;
        } else {
            // network/5xx pause: leave bookmark where it is, drop out
            // of the loop, and transition back to ready_to_sync so the
            // next processQueue tick picks the session up again.
            // (processQueue only queries by status="ready_to_sync";
            // leaving the status as "syncing" would orphan the session
            // until manual intervention.)
            await transitionToReadyToSync(session);
            return;
        }
    }
    // All steps completed — Phase 6.4 cleans the row up.
    await deleteSession(session.id);
    // Drop the active pointer along with the row it names. CheckSummary
    // deliberately leaves it set through the in-flight period so the UI
    // keeps serving photos from IDB while the session is ready_to_sync;
    // once the row is gone a surviving pointer makes every
    // `getActiveSessionId()` consumer read a dead session — addPhoto
    // throws ("offline session not found", surfacing as "Failed to save
    // Image") and getSessionPhotosForTarget returns [] instead of letting
    // the caller fall back to the server. Only clear our own id: another
    // session may have become active while this one replayed.
    if (getActiveSessionId() === session.id) {
        setActiveSessionId(null);
    }
    // Fire AFTER the row is gone so subscribers (e.g. LayerIndex) that
    // re-fetch the affected layer see a queue with no `syncing` /
    // `ready_to_sync` row for it and the banner is in its final state.
    emitSuccess({
        sessionId: session.id,
        cachedLayerId: session.cached_layer.id,
    });
}

async function transitionToReadyToSync(
    s: OfflineCheckSession,
): Promise<void> {
    const { getOfflineDB, STORE_SESSIONS } = await import("./db");
    const db = await getOfflineDB();
    const stored = await db.get(STORE_SESSIONS, s.id);
    if (!stored) return;
    stored.status = "ready_to_sync";
    await db.put(STORE_SESSIONS, stored);
}

async function transitionToSyncing(
    s: OfflineCheckSession,
): Promise<OfflineCheckSession> {
    const { getOfflineDB, STORE_SESSIONS } = await import("./db");
    const db = await getOfflineDB();
    const stored = await db.get(STORE_SESSIONS, s.id);
    if (!stored) return s;
    stored.status = "syncing";
    await db.put(STORE_SESSIONS, stored);
    return stored;
}

async function advanceBookmark(
    session: OfflineCheckSession,
    nextStep: number,
    idMap?: ReplayIdMap,
): Promise<OfflineCheckSession> {
    const next = idMap ?? session.replay_id_map;
    await updateReplayBookmark(session.id, nextStep, next);
    return { ...session, replay_bookmark: nextStep, replay_id_map: next };
}

async function markFailed(
    session: OfflineCheckSession,
    reason: string,
): Promise<void> {
    // We persist failure via the existing session record. Reuses
    // sessionManager's putSession indirectly through updateReplayBookmark,
    // which is the only persistence call the engine has — for status
    // changes we go through the raw IDB path here to keep the public
    // session-manager API surface minimal. We import getOfflineDB lazily
    // to avoid a circular dependency.
    const { getOfflineDB, STORE_SESSIONS } = await import("./db");
    const db = await getOfflineDB();
    const stored = await db.get(STORE_SESSIONS, session.id);
    if (!stored) return;
    stored.status = "failed";
    stored.error = reason;
    await db.put(STORE_SESSIONS, stored);
}

type StepOutcome =
    | { kind: "ok"; idMap?: ReplayIdMap }
    | { kind: "fail"; reason: string }
    | { kind: "pause" };

function classifyAxiosError(err: unknown): "network" | "client" | "server" {
    const e = err as { response?: { status?: number }; request?: unknown };
    if (!e?.response) return "network";
    const status = e.response.status ?? 0;
    if (status >= 400 && status < 500) return "client";
    return "server";
}

async function withRetries<T>(
    fn: () => Promise<T>,
    onClientError: (err: unknown) => string,
    maxRetries = 3,
): Promise<T | { _failed: string } | { _paused: true }> {
    let attempts = 0;
    // Retry loop — exit conditions are early returns inside the catch.
    // eslint-disable-next-line no-constant-condition
    while (true) {
        try {
            return await fn();
        } catch (err) {
            const cls = classifyAxiosError(err);
            if (cls === "network") {
                return { _paused: true };
            }
            if (cls === "client") {
                return { _failed: onClientError(err) };
            }
            // server: retry with backoff
            attempts += 1;
            if (attempts >= maxRetries) {
                return { _paused: true };
            }
            const delayMs = attempts * 500;
            await new Promise<void>((res) => {
                setTimeout(res, delayMs);
            });
        }
    }
}

function isFailed<T>(r: T | { _failed: string } | { _paused: true }): r is { _failed: string } {
    return typeof (r as { _failed?: string })._failed === "string";
}
function isPaused<T>(r: T | { _failed: string } | { _paused: true }): r is { _paused: true } {
    return (r as { _paused?: true })._paused === true;
}

async function runStep(
    session: OfflineCheckSession,
    step: number,
): Promise<StepOutcome> {
    const idMap = { ...session.replay_id_map, fruit_ids: { ...session.replay_id_map.fruit_ids } };
    switch (step) {
    case 0: return runVerifyConfig(session);
    case 1: return runResolveBarcode(session, idMap);
    case 2: return runFetchLayer(session, idMap);
    case 3: return runCreateCheck(session, idMap);
    case 4: return runCreateFruits(session, idMap);
    case 5: return runPatchFruitFields(session);
    case 6: return runRegisterDefects(session);
    case 7: return runUploadPhotos(session);
    case 8: return runPatchCheckMeta(session);
    case 9: return runApplyBusinessRules(session);
    case 10: return runCompleteCheck(session);
    case 11: return runGroupMatchingSiblings(session);
    case 12: return runAllocateToStage(session);
    default: return { kind: "fail", reason: `unknown replay step ${step}` };
    }
}

// ───────────────────────── steps ─────────────────────────

async function runVerifyConfig(
    session: OfflineCheckSession,
): Promise<StepOutcome> {
    // Re-fetch /configs/active and compare ETag against the
    // session.config_hash captured at scan time. The ETag interceptor
    // would normally handle this transparently; we bypass that by
    // calling axios directly and reading the response header so we
    // can compare with the captured value before deciding.
    const result = await withRetries(
        async () => {
            // We deliberately do NOT use the apiClient here — its ETag
            // interceptor would convert a 304 into the cached body and
            // we'd miss the header comparison. Use a plain fetch.
            const res = await fetch(`${API_BASE}/tenants/${getTenantId()}/configs/active`, {
                method: "GET",
                headers: await authHeaders(),
            });
            if (!res.ok && res.status !== 304) {
                const err: any = new Error(`HTTP ${res.status}`);
                err.response = { status: res.status };
                throw err;
            }
            return res.headers.get("etag");
        },
        () => "Could not reach the server to verify the form configuration.",
    );
    if (isPaused(result)) return { kind: "pause" };
    if (isFailed(result)) return { kind: "fail", reason: result._failed };

    const currentHash = result as string | null;
    if (!currentHash) {
        return { kind: "fail", reason: "Server did not return a config hash." };
    }
    if (currentHash !== session.check_config.config_hash) {
        return {
            kind: "fail",
            reason: "The form configuration has changed since this check was started offline. Please perform this check again.",
        };
    }
    return { kind: "ok" };
}

async function runResolveBarcode(
    session: OfflineCheckSession,
    idMap: ReplayIdMap,
): Promise<StepOutcome> {
    const result = await withRetries(
        async () => apiClient.get(`${API_BASE}/layers/search-label/`, {
            params: { label: session.barcode },
        }),
        () => "Pallet not found on the server.",
    );
    if (isPaused(result)) return { kind: "pause" };
    if (isFailed(result)) return { kind: "fail", reason: result._failed };
    const layerId = (result as { data?: { layer_id?: string } }).data?.layer_id;
    if (!layerId) {
        return { kind: "fail", reason: "Pallet not found on the server." };
    }
    if (layerId !== session.cached_layer.id) {
        // eslint-disable-next-line no-console
        console.warn(
            `[replay] layer_id changed since cache: cached=${session.cached_layer.id}, server=${layerId}`,
        );
    }
    return { kind: "ok", idMap: { ...idMap, layer_id: layerId } };
}

async function runFetchLayer(
    session: OfflineCheckSession,
    idMap: ReplayIdMap,
): Promise<StepOutcome> {
    const layerId = idMap.layer_id;
    if (!layerId) return { kind: "fail", reason: "internal: layer_id missing at step 2" };
    const result = await withRetries(
        // No trailing slash: the route is registered at `/{layer_id}` in
        // backend/api/web/layers/layers.py. With trailing slash FastAPI
        // emits a 307 redirect, and because nginx terminates HTTPS the
        // redirect's `Location` header uses scheme `http` and no port,
        // sending the browser to `http://avos.local/api/...` →
        // ERR_CONNECTION_REFUSED. See EFQ-2962 local validation.
        async () => apiClient.get(`${API_BASE}/layers/${layerId}`),
        () => "Could not load the pallet from the server.",
    );
    if (isPaused(result)) return { kind: "pause" };
    if (isFailed(result)) return { kind: "fail", reason: result._failed };
    const fruitType = (result as { data?: { fruit_type?: string } }).data?.fruit_type;
    if (
        session.cached_layer.fruit_type
        && fruitType
        && fruitType !== session.cached_layer.fruit_type
    ) {
        return {
            kind: "fail",
            reason: "The fruit type of this pallet changed since the offline check was started.",
        };
    }
    return { kind: "ok" };
}

async function runCreateCheck(
    session: OfflineCheckSession,
    idMap: ReplayIdMap,
): Promise<StepOutcome> {
    if (!idMap.layer_id) return { kind: "fail", reason: "internal: layer_id missing at step 3" };
    const body = {
        layer_id: idMap.layer_id,
        location: session.check_config.location,
        location_id: session.check_config.location_id,
        is_manual: session.check_config.is_manual,
        idempotency_key: session.check_config.idempotency_key,
        client_created_at: new Date(session.created_at).toISOString(),
        meta: session.check_config.meta,
        box_size: session.check_config.box_size,
        tracebility: session.check_config.tracebility,
        weight_kg: session.check_config.weight_kg,
        pallet_integrety: session.check_config.pallet_integrety,
        box_integrity: session.check_config.box_integrity,
        temperature: session.check_config.temperature,
    };
    const result = await withRetries(
        async () => apiClient.post(`${API_BASE}/checks/`, body),
        () => "Server rejected the check creation request.",
    );
    if (isPaused(result)) return { kind: "pause" };
    if (isFailed(result)) return { kind: "fail", reason: result._failed };
    const realCheckId = (result as { data?: { check_id?: number } }).data?.check_id;
    if (typeof realCheckId !== "number") {
        return { kind: "fail", reason: "Server did not return a check id." };
    }
    return { kind: "ok", idMap: { ...idMap, check_id: realCheckId } };
}

async function runCreateFruits(
    session: OfflineCheckSession,
    idMap: ReplayIdMap,
): Promise<StepOutcome> {
    const realCheckId = idMap.check_id;
    if (typeof realCheckId !== "number") {
        return { kind: "fail", reason: "internal: check_id missing at step 4" };
    }
    const fruitIds = { ...idMap.fruit_ids };
    for (const fruit of session.fruits) {
        if (fruitIds[fruit.idempotency_key]) continue; // already created on a previous attempt
        const body = {
            check_id: realCheckId,
            n: 1,
            unit_of_account: fruit.unit_of_account,
            idempotency_key: fruit.idempotency_key,
            client_created_at: fruit.created_at,
        };
        const result = await withRetries(
            async () => apiClient.post(`${API_BASE}/avocados/manual/`, body),
            () => "Server rejected one of the fruit-creation requests.",
        );
        if (isPaused(result)) return { kind: "pause" };
        if (isFailed(result)) return { kind: "fail", reason: result._failed };
        const realAvocadoId = (result as { data?: { avocado_id?: number } }).data?.avocado_id;
        if (typeof realAvocadoId !== "number") {
            return { kind: "fail", reason: "Server did not return an avocado id." };
        }
        fruitIds[fruit.idempotency_key] = realAvocadoId;
    }
    return { kind: "ok", idMap: { ...idMap, fruit_ids: fruitIds } };
}

async function runPatchFruitFields(
    session: OfflineCheckSession,
): Promise<StepOutcome> {
    for (const fruit of session.fruits) {
        const realAvocadoId = session.replay_id_map.fruit_ids[fruit.idempotency_key];
        if (!realAvocadoId) continue;
        for (const [field, value] of Object.entries(fruit.fields)) {
            const result = await withRetries(
                async () => apiClient.patch(`${API_BASE}/avocados/${realAvocadoId}/`, {
                    field,
                    value,
                    state_value: value,
                }),
                () => `Server rejected a fruit field update (${field}).`,
            );
            if (isPaused(result)) return { kind: "pause" };
            if (isFailed(result)) return { kind: "fail", reason: result._failed };
        }
    }
    return { kind: "ok" };
}

async function runRegisterDefects(
    session: OfflineCheckSession,
): Promise<StepOutcome> {
    const realCheckId = session.replay_id_map.check_id;
    if (typeof realCheckId !== "number") {
        return { kind: "fail", reason: "internal: check_id missing at step 6" };
    }
    for (const fruit of session.fruits) {
        const realAvocadoId = session.replay_id_map.fruit_ids[fruit.idempotency_key];
        if (!realAvocadoId) continue;
        if (!fruit.defects.length) continue;
        const flatIds: string[] = [];
        const flatPositions: string[] = [];
        const flatSeverities: string[] = [];
        for (const defect of fruit.defects) {
            const count = Math.max(1, defect.frequency);
            for (let i = 0; i < count; i += 1) {
                flatIds.push(defect.defect_id);
                flatPositions.push(defect.positions?.[i] ?? "");
                flatSeverities.push(defect.severities?.[i] ?? "");
            }
        }
        const body = {
            check_id: realCheckId,
            fruit_id: realAvocadoId,
            defect_ids: flatIds,
            positions: flatPositions,
            severities: flatSeverities,
            unregister_others: true,
        };
        const result = await withRetries(
            async () => apiClient.post(`${API_BASE}/defects/register-bulk`, body),
            () => "Server rejected the defects registration.",
        );
        if (isPaused(result)) return { kind: "pause" };
        if (isFailed(result)) return { kind: "fail", reason: result._failed };
    }

    // Check-level (visual / intake) defects: registered against the check
    // itself with fruit_id null. `register_defect` is a last-write-wins
    // upsert per (check_id, defect_id), so replaying the whole set is
    // idempotent — a retried step 6 lands the same final state.
    const checkDefects = session.check_defects ?? {};
    for (const [defectId, frequency] of Object.entries(checkDefects)) {
        const body = {
            check_id: realCheckId,
            fruit_id: null,
            defect_id: defectId,
            frequency,
        };
        const result = await withRetries(
            async () => apiClient.post(`${API_BASE}/defects/register`, body),
            () => "Server rejected the check defects registration.",
        );
        if (isPaused(result)) return { kind: "pause" };
        if (isFailed(result)) return { kind: "fail", reason: result._failed };
    }
    return { kind: "ok" };
}

async function runUploadPhotos(
    session: OfflineCheckSession,
): Promise<StepOutcome> {
    if (session.photos.length === 0) return { kind: "ok" };
    const realCheckId = session.replay_id_map.check_id;
    if (typeof realCheckId !== "number") {
        return { kind: "fail", reason: "internal: check_id missing at step 7" };
    }
    for (const photo of session.photos) {
        // Resolve target id: for "fruit" photos, look up the real
        // avocado_id via the fruits id_map (the temp_avocado_id at
        // capture time matches a fruit's idempotency_key in the
        // session). For "check" photos, the real check_id is fine.
        let url: string;
        let body: Record<string, unknown>;
        if (photo.target === "fruit") {
            const fruit = session.fruits.find(
                (f) => f.temp_avocado_id === photo.target_temp_id,
            );
            const realAvocadoId = fruit
                ? session.replay_id_map.fruit_ids[fruit.idempotency_key]
                : undefined;
            if (typeof realAvocadoId !== "number") {
                return {
                    kind: "fail",
                    reason: `internal: real avocado_id missing for photo at temp id ${photo.target_temp_id}`,
                };
            }
            url = `${API_BASE}/images/fruit/${realAvocadoId}/image/`;
            body = {
                avocado_id: realAvocadoId,
                type: photo.type,
                base64: photo.base64,
                focusMode: photo.focusMode,
            };
        } else {
            url = `${API_BASE}/images/check/${realCheckId}/add_image/`;
            body = {
                check_id: realCheckId,
                type: photo.type,
                base64: photo.base64,
                focusMode: photo.focusMode,
            };
        }
        const result = await withRetries(
            async () => apiClient.post(url, body),
            () => `Server rejected a photo upload (${photo.type}).`,
        );
        if (isPaused(result)) return { kind: "pause" };
        if (isFailed(result)) return { kind: "fail", reason: result._failed };
    }
    return { kind: "ok" };
}

async function runPatchCheckMeta(
    session: OfflineCheckSession,
): Promise<StepOutcome> {
    const realCheckId = session.replay_id_map.check_id;
    if (typeof realCheckId !== "number") {
        return { kind: "fail", reason: "internal: check_id missing at step 8" };
    }
    for (const [field, value] of Object.entries(session.check_fields)) {
        const result = await withRetries(
            async () => apiClient.patch(`${API_BASE}/checks/${realCheckId}/`, {
                field,
                value,
                state_value: value,
            }),
            () => `Server rejected a check field update (${field}).`,
        );
        if (isPaused(result)) return { kind: "pause" };
        if (isFailed(result)) return { kind: "fail", reason: result._failed };
    }
    return { kind: "ok" };
}

/**
 * Colour flags and the other business-rule output are only written when
 * the rules run. Online, CheckSummary triggers them right before
 * `/complete`; the offline path has no such caller, so without this step
 * a synced check stays grey. Placed BEFORE complete_check (mirroring the
 * online order) because complete may send the check's status/category to
 * an external service, and those are computed by the rules.
 *
 * The endpoint is an idempotent recompute (force=True), so a retry is safe.
 * A 4xx is not allowed to fail the session: online the response is
 * ignored too, and the dirty-check cron remains a fallback.
 */
async function runApplyBusinessRules(
    session: OfflineCheckSession,
): Promise<StepOutcome> {
    const realCheckId = session.replay_id_map.check_id;
    if (typeof realCheckId !== "number") {
        return { kind: "fail", reason: "internal: check_id missing at business rules step" };
    }
    const result = await withRetries(
        async () => apiClient.get(`${API_BASE}/checks/${realCheckId}/business-rules`),
        () => "Server rejected the business rules request.",
    );
    if (isPaused(result)) return { kind: "pause" };
    // isFailed (4xx): intentionally non-fatal, see above.
    return { kind: "ok" };
}

async function runCompleteCheck(
    session: OfflineCheckSession,
): Promise<StepOutcome> {
    const realCheckId = session.replay_id_map.check_id;
    if (typeof realCheckId !== "number") {
        return { kind: "fail", reason: "internal: check_id missing at step 10" };
    }
    const result = await withRetries(
        async () => apiClient.post(`${API_BASE}/checks/${realCheckId}/complete`, {}),
        () => "Server rejected the complete-check request.",
    );
    if (isPaused(result)) return { kind: "pause" };
    if (isFailed(result)) return { kind: "fail", reason: result._failed };
    return { kind: "ok" };
}

async function runGroupMatchingSiblings(
    session: OfflineCheckSession,
): Promise<StepOutcome> {
    if (!session.retrospective_group_matching_siblings) return { kind: "ok" };
    const realCheckId = session.replay_id_map.check_id;
    if (typeof realCheckId !== "number") {
        return { kind: "fail", reason: "internal: check_id missing at grouping step" };
    }
    const result = await withRetries(
        async () => apiClient.post(`${API_BASE}/${getTenantId()}/checks/${realCheckId}/retrospective-group`, {
            attach_all_matching: true,
            group_by_attributes: session.retrospective_group_by_attributes,
        }),
        () => "Server rejected grouping this check with matching siblings.",
    );
    if (isPaused(result)) return { kind: "pause" };
    if (isFailed(result)) return { kind: "fail", reason: result._failed };
    return { kind: "ok" };
}

/**
 * The check type's `allocate_to_stage`, which online StartCheckForm POSTs
 * when the check starts. Last in STEPS so no persisted bookmark shifts
 * (see STEPS); nothing earlier depends on the pallet's stage, and the
 * check's location link is captured at start, before any allocation.
 *
 * Which layers to allocate is resolved here against the server's layer,
 * with the same rule as online: the offline layer cache carries neither
 * parents nor the current stage. That also makes a retry safe — once the
 * layer is at the stage it is skipped, and the server ignores a repeated
 * stage-only allocation to the location a layer is already at.
 *
 * Online a failed allocation is only logged and the check goes on, so a
 * 4xx here doesn't fail the (already completed) check either.
 */
async function runAllocateToStage(
    session: OfflineCheckSession,
): Promise<StepOutcome> {
    const allocation = session.stage_allocation;
    if (!allocation) return { kind: "ok" };
    const layerId = session.replay_id_map.layer_id;
    if (!layerId) return { kind: "fail", reason: "internal: layer_id missing at allocation step" };

    const layerResult = await withRetries(
        async () => apiClient.get(`${API_BASE}/layers/${layerId}`),
        () => "Could not load the pallet to allocate it to a stage.",
    );
    if (isPaused(layerResult)) return { kind: "pause" };
    if (isFailed(layerResult)) {
        // eslint-disable-next-line no-console
        console.warn(`[replay] stage allocation skipped: ${layerResult._failed}`);
        return { kind: "ok" };
    }
    const layer = (layerResult as { data?: object }).data ?? {};

    const requests = stageAllocationRequests(allocation.stage, allocation.parent_types, { ...layer, id: layerId });
    for (const request of requests) {
        const result = await withRetries(
            async () => apiClient.post(`${API_BASE}/layers/${request.layerId}/locations`, {
                layer_id: request.layerId,
                stage: request.stage,
                include_children: request.includeChildren ?? false,
            }),
            () => `Server rejected allocating the pallet to ${request.stage}.`,
        );
        if (isPaused(result)) return { kind: "pause" };
        if (isFailed(result)) {
            // eslint-disable-next-line no-console
            console.warn(`[replay] ${result._failed}`);
        }
    }
    return { kind: "ok" };
}

// ───────────────────────── helpers ─────────────────────────

function getTenantId(): string {
    // Tenant id isn't on the session record (it's implicit in the
    // auth token). We read it from the same Redux store the
    // configHook uses. Imported lazily to avoid pulling Redux into
    // module load.
    // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
    const { getStore } = require("../store");
    const state = getStore()?.getState?.();
    return state?.auth?.user?.tenant?.tenant_id ?? "";
}

async function authHeaders(): Promise<Record<string, string>> {
    // Demo mode has no MSAL account to acquire a token from, and the backend
    // resolves the demo user without one (AUTH_IS_DEMO — see the matching
    // IS_DEMO branch in constants/apiClient). Without this the replay stalls
    // silently rather than failing: acquireTokenSilent rejects, withRetries
    // treats it as a pause-and-retry, and the session sits at ready_to_sync
    // forever with no request ever leaving the browser. This is the only
    // request in the app that builds its own auth headers instead of going
    // through apiClient, which is why it was the only one demo mode missed.
    if (IS_DEMO) return {};

    // ES dynamic import (NOT CommonJS `require`): msal-instance.js has a
    // top-level `await msalInstance.initialize()` and require() doesn't
    // wait for that, so the named export comes back as `undefined`
    // ("Cannot read properties of undefined (reading 'acquireTokenSilent')"
    // — observed during EFQ-2962 local validation).
    const [{ msalInstance }, { scopes, tokenType }] = await Promise.all([
        import("../msal-instance"),
        import("../authConfig"),
    ]);
    // Don't swallow the failure: if we can't acquire a token, sending
    // the request with no Authorization header gets a clean 401 from
    // the server, which the replay engine surfaces as "Sync failed:
    // Could not reach the server..." — confusing because the network
    // is up. Throw a marked error instead so withRetries can pause and
    // retry while the React app refreshes the MSAL session.
    const token = await msalInstance.acquireTokenSilent({ scopes });
    return { Authorization: `Bearer ${token[tokenType]}` };
}

/** For tests only. Reset listeners + the in-flight flag. */
export function _resetReplayEngineForTests(): void {
    progressListeners.clear();
    failureListeners.clear();
    successListeners.clear();
    processing = false;
}

export type { OfflineCheckSession, OfflineFruit }; // re-export for test convenience
